import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState, type ChangeEvent } from "react";
import { formatCents, LoadingPanel, PayoutStateBadge } from "./shared";
import type { PayoutRow } from "./types";

type View = "pending" | "history";
type StatusMessage = { type: "success" | "error"; text: string };

// Human copy for the structured execute failure codes (plan §5/§6).
const EXECUTE_ERROR_COPY: Record<string, string> = {
  insufficient_balance:
    "Insufficient platform balance to fund this payout. Top up the Stripe financial account, then retry.",
  recipient_restricted:
    "The recipient's payout account is restricted or not yet active — they must finish onboarding before this can be sent.",
  already_executed: "This payout has already been executed.",
};

export function PayoutsPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [view, setView] = useState<View>("pending");
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [externalTarget, setExternalTarget] = useState<PayoutRow | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  // Pending = scheduled runs awaiting approval; history = everything else.
  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-payouts", view],
    queryFn: () =>
      api.affiliates.listPayouts({ state: view === "pending" ? "scheduled" : "all" }),
  });

  const allRows: PayoutRow[] = data?.payouts ?? [];
  const rows =
    view === "pending"
      ? allRows.filter((p) => p.state === "scheduled")
      : allRows.filter((p) => p.state !== "scheduled");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["affiliate-payouts"] });

  // Two-step approval: over the run hard cap the API replies 409 with the cap +
  // amount, which we surface as a SECOND red confirm before re-POSTing with
  // confirm:true (plan §3/§4).
  const handleApprove = async (row: PayoutRow) => {
    const label = row.affiliateEmail || row.affiliateId;
    const ok = await confirm({
      title: `Approve ${formatCents(row.amountCents, row.currency)} payout?`,
      description: `Approving stamps the approver on the run for ${label}. Execution (the actual transfer) happens in A7Y-1899.`,
      confirmLabel: "Approve",
      confirmColor: "green",
    });
    if (!ok) return;

    setApprovingId(row.id);
    try {
      const res = await api.affiliates.approvePayout(row.id);
      if (res.ok) {
        invalidate();
        showStatus("success", "Payout approved");
        return;
      }
      // Over hard cap — require an explicit second confirmation.
      const confirmOverCap = await confirm({
        title: "Payout exceeds the run hard cap",
        description: `This payout of ${formatCents(res.amount, res.currency)} exceeds the ${formatCents(res.cap, res.currency)} run hard cap. Approve anyway?`,
        confirmLabel: "Approve over cap",
        confirmColor: "red",
      });
      if (!confirmOverCap) return;
      const res2 = await api.affiliates.approvePayout(row.id, true);
      if (res2.ok) {
        invalidate();
        showStatus("success", "Payout approved (over cap)");
      } else {
        showStatus("error", "Approval still blocked by the API");
      }
    } catch (err) {
      showStatus("error", `Approve failed: ${(err as Error).message}`);
    } finally {
      setApprovingId(null);
    }
  };

  // Execution is the SECOND, deliberate step after approval and moves REAL
  // money irreversibly (plan §5/§6) — gated behind a red confirm distinct from
  // the green approve confirm.
  const handleExecute = async (row: PayoutRow) => {
    const label = row.affiliateEmail || row.affiliateId;
    const ok = await confirm({
      title: `Execute ${formatCents(row.amountCents, row.currency)} payout?`,
      description: `This sends real money to ${label} via Stripe. It is IRREVERSIBLE — once the transfer is accepted the funds cannot be recalled.`,
      confirmLabel: "Execute payout",
      confirmColor: "red",
    });
    if (!ok) return;

    setExecutingId(row.id);
    try {
      const res = await api.affiliates.executePayout(row.id);
      if (res.ok) {
        invalidate();
        showStatus("success", "Payout execution started");
        return;
      }
      showStatus("error", EXECUTE_ERROR_COPY[res.error] ?? `Execution failed: ${res.error}`);
    } catch (err) {
      showStatus("error", `Execute failed: ${(err as Error).message}`);
    } finally {
      setExecutingId(null);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await api.affiliates.exportPayoutsCsv({
        state: view === "pending" ? "scheduled" : undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showStatus("error", `Export failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="px-4 py-0">
      <PageHeader title="Payouts" subtitle="Approve payout runs and record external payments">
        <Button outline onClick={handleExport}>
          <Download data-slot="icon" />
          Export CSV
        </Button>
      </PageHeader>

      <div className="mb-6 flex gap-0 border-b border-zinc-200 dark:border-zinc-700">
        {(["pending", "history"] as View[]).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
              view === v
                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Affiliate</TableHeader>
              <TableHeader>State</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Commissions</TableHeader>
              {view === "pending" ? (
                <TableHeader>Created</TableHeader>
              ) : (
                <>
                  <TableHeader>Approved</TableHeader>
                  <TableHeader>Stripe payout</TableHeader>
                </>
              )}
              <TableHeader className="text-right">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={view === "pending" ? 6 : 7} className="py-8 text-center">
                  <Text className="text-sm text-zinc-500">
                    {view === "pending" ? "No pending payout runs" : "No payout history"}
                  </Text>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-sm">
                    {p.affiliateEmail || <span className="font-mono text-xs">{p.affiliateId}</span>}
                  </TableCell>
                  <TableCell>
                    <PayoutStateBadge state={p.state} />
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatCents(p.amountCents, p.currency)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500">{p.commissionIds.length}</TableCell>
                  {view === "pending" ? (
                    <TableCell className="text-sm text-zinc-500">{formatDate(p.createdAt)}</TableCell>
                  ) : (
                    <>
                      <TableCell className="text-sm text-zinc-500">
                        {p.approvedAt ? formatDate(p.approvedAt) : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {p.stripePayoutId || (p.state === "failed" ? "FAILED" : "—")}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {view === "pending" && !p.approvedBy && (
                        <Button
                          color="green"
                          disabled={approvingId === p.id}
                          onClick={() => handleApprove(p)}
                        >
                          {approvingId === p.id ? "Approving..." : "Approve"}
                        </Button>
                      )}
                      {p.approvedBy && p.state === "scheduled" && (
                        <Button
                          color="red"
                          disabled={executingId === p.id}
                          onClick={() => handleExecute(p)}
                        >
                          {executingId === p.id ? "Executing..." : "Execute"}
                        </Button>
                      )}
                      {p.state !== "paid" && (
                        <Button outline onClick={() => setExternalTarget(p)}>
                          Mark paid externally
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {externalTarget && (
        <MarkExternalDialog
          payout={externalTarget}
          onClose={() => setExternalTarget(null)}
          onSaved={() => {
            invalidate();
            setExternalTarget(null);
            showStatus("success", "Payout marked paid externally");
          }}
          onError={(msg) => showStatus("error", msg)}
        />
      )}
    </div>
  );
}

function MarkExternalDialog({
  payout,
  onClose,
  onSaved,
  onError,
}: {
  payout: PayoutRow;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const invalid = method.trim() === "" || reference.trim() === "";

  const mutation = useMutation({
    mutationFn: () =>
      api.affiliates.markPayoutExternal(payout.id, {
        method: method.trim(),
        reference: reference.trim(),
      }),
    onSuccess: onSaved,
    onError: (err: Error) => onError(`Mark-paid failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} size="md">
      <DialogTitle>Mark payout paid externally</DialogTitle>
      <DialogBody>
        <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          Records that this {formatCents(payout.amountCents, payout.currency)} payout was settled
          outside Stripe (e.g. wire/PayPal). Sets the run to <strong>paid</strong> and stores the
          method + reference for audit.
        </Text>
        <Field>
          <Label>Payment method</Label>
          <Input
            value={method}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setMethod(e.target.value)}
            placeholder="e.g. wire, paypal, manual"
          />
        </Field>
        <Field className="mt-4">
          <Label>Reference</Label>
          <Input
            value={reference}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setReference(e.target.value)}
            placeholder="External transaction reference"
          />
        </Field>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button color="blue" disabled={invalid || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? "Saving..." : "Mark paid"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
